/**
 * cart-cross-sell.js
 * Revolution Engineering — Diesel Additive Holders cart logic
 *
 * 1. Ford upsell: when a magnetic-mount item is added to the cart, show a
 *    non-blocking side popup ("2017+ Ford? You'll need one of these too!")
 *    with a product card for the Ford Magnetic Mount Plate that the
 *    customer can add to cart with one click, or dismiss.
 * 2. Incompatibility warning: if the cart ever contains the Ford Magnetic
 *    Mount Plate AND any screw-on additive holder, show a forced "OK" modal
 *    explaining the two products are incompatible and work independently.
 * 3. Free shipping progress bar: shown in the cart notification popup and
 *    on the full cart page.
 * 4. Hot Shot's Secret bottle upsell: when a Hotshot's Secret EDT Magnetic
 *    Diesel Additive Holder is added to the cart, show a non-blocking side
 *    popup recommending the Hot Shot's Secret bottle sized to fit that
 *    holder (16 fl oz, 32 fl oz, or 64 fl oz), so customers don't buy a
 *    holder without the additive that goes in it. The 16oz and 32oz holders
 *    each fit two different bottles equally well (the standard EDT bottle
 *    in that size and the EDT+ Winter Defense bottle in that size), so
 *    those two are resolved by getBottleForSize() based on season. 64oz
 *    only has one matching bottle, so it's a fixed lookup.
 * 5. Free shipping cart-page upsell: on the full cart page, once the free
 *    shipping bar is showing a remaining amount, show one or more product
 *    cards to help close the gap. If the cart contains a Hot Shot's Secret
 *    EDT holder without its matching bottle, that bottle is shown first —
 *    it's a relevant pairing regardless of price. The Magnetic Funnel
 *    Holder, the Ford Super Duty Magnetic Mount Plate, and the Revolution
 *    Engineering T-Shirt are always offered underneath it (skipping any
 *    that are already in the cart), since they're useful to any customer
 *    regardless of which additive brand they use.
 * 6. Attribution: every item added through an upsell carries a hidden
 *    line-item property (_source = ford-popup | bottle-popup | cart-upsell).
 *    Underscore-prefixed properties are not shown to the customer at
 *    checkout but appear on the order in Shopify admin.
 */
(function () {
  if (window.__reCartCrossSellInit) return;
  window.__reCartCrossSellInit = true;

  var FORD_PLATE_HANDLE = '2017-ford-super-duty-magnetic-mount-plate';
  var FORD_PLATE_VARIANT_ID = 54032721772757;
  var FORD_PLATE_TITLE = '2017+ Ford Super Duty Magnetic Mount Plate';
  var FORD_PLATE_PRICE = '$29.99';
  var FORD_PLATE_IMAGE_URL = 'https://cdn.shopify.com/s/files/1/0739/7680/7637/files/IMG_0452.jpg?v=1781286976';
  var FORD_PLATE_URL = '/products/' + FORD_PLATE_HANDLE;
  var FORD_PROMPT_SESSION_KEY = 're_ford_prompt_shown';
  var INCOMPAT_SHOWN_SESSION_KEY = 're_incompat_combo_shown';

  // Domestic free-shipping threshold, sourced from the "Economy" shipping
  // rate condition (TOTAL_PRICE >= $99) configured in Shopify shipping settings.
  // Update this value if that rate's threshold ever changes.
  var FREE_SHIPPING_THRESHOLD_CENTS = 9900;

  // ---------- Hot Shot's Secret holder -> bottle upsell ----------

  // Maps each variant of the "Hotshot's Secret EDT Magnetic Diesel Additive
  // Holder" (SKUs HS16M/HS16S/HS32M/HS32S/HS64M/HS64S) to its bottle size
  // bucket, so we can recommend the Hot Shot's Secret bottle that's sized
  // to actually fit the holder the customer just added.
  var HOLDER_VARIANT_SIZE_MAP = {
    51504568402133: '16', // 16 fl oz / Magnet
    51504568434901: '16', // 16 fl oz / Screw
    51504568467669: '32', // 32 fl oz / Magnet
    51504568500437: '32', // 32 fl oz / Screw
    52254967136469: '64', // 64 fl oz / Magnet
    52254967169237: '64'  // 64 fl oz / Screw
  };

  // Bottle recommended per holder size, for sizes that only have one
  // matching Hot Shot's Secret bottle. 64oz -> Diesel Extreme 64oz (the
  // only bottle sold in 64oz). 16oz and 32oz each have two equally-fitting
  // bottles and are resolved separately by getBottleForSize() based on
  // season.
  var HOLDER_SIZE_BOTTLE_MAP = {
    '64': {
      variantId: 54111394627797,
      productHandle: 'hot-shots-secret-diesel-extreme',
      title: "Hot Shot's Secret Diesel Extreme — 64oz (2QT)",
      price: '$45.99',
      imageUrl: 'https://cdn.shopify.com/s/files/1/0739/7680/7637/files/P040464Z-1.jpg?v=1782402149'
    }
  };

  // 16oz holder recommendation, year-round default: Everyday Diesel
  // Treatment 16oz Squeeze.
  var BOTTLE_16_STANDARD = {
    variantId: 54111394398421,
    productHandle: 'hot-shots-secret-everyday-diesel-treatment',
    title: "Hot Shot's Secret Everyday Diesel Treatment — 16oz Squeeze",
    price: '$22.99',
    imageUrl: 'https://cdn.shopify.com/s/files/1/0739/7680/7637/files/HSSEDT16ZSP.jpg?v=1782399640'
  };

  // 16oz holder recommendation, October through March only: EDT+ Winter
  // Defense 16oz Squeeze.
  var BOTTLE_16_WINTER = {
    variantId: 54111394431189,
    productHandle: 'hot-shots-secret-edt-winter-defense',
    title: "Hot Shot's Secret EDT+ Winter Defense — 16oz Squeeze",
    price: '$19.99',
    imageUrl: 'https://cdn.shopify.com/s/files/1/0739/7680/7637/files/EDTWAG16OZSQ_Front_1_600.jpg?v=1782399646'
  };

  // 32oz holder recommendation, year-round default: Diesel Extreme 32oz.
  var BOTTLE_32_STANDARD = {
    variantId: 54111394595029,
    productHandle: 'hot-shots-secret-diesel-extreme',
    title: "Hot Shot's Secret Diesel Extreme — 32oz (1QT)",
    price: '$27.99',
    imageUrl: 'https://cdn.shopify.com/s/files/1/0739/7680/7637/files/P040432Z_01.jpg?v=1782399652'
  };

  // 32oz holder recommendation, October through March only: EDT+ Winter
  // Defense 32oz, since cold-weather anti-gel protection is the more
  // seasonally relevant pick during those months.
  var BOTTLE_32_WINTER = {
    variantId: 54111394463957,
    productHandle: 'hot-shots-secret-edt-winter-defense',
    title: "Hot Shot's Secret EDT+ Winter Defense — 32oz (1QT)",
    price: '$28.99',
    imageUrl: 'https://cdn.shopify.com/s/files/1/0739/7680/7637/files/EDTWAG32OZ_01.jpg?v=1782402284'
  };

  // True for October (10) through March (3) inclusive, using the
  // customer's local clock.
  function isWinterUpsellWindow() {
    var month = new Date().getMonth() + 1;
    return month >= 10 || month <= 3;
  }

  function getBottleForSize(size) {
    if (size === '16') {
      return isWinterUpsellWindow() ? BOTTLE_16_WINTER : BOTTLE_16_STANDARD;
    }
    if (size === '32') {
      return isWinterUpsellWindow() ? BOTTLE_32_WINTER : BOTTLE_32_STANDARD;
    }
    return HOLDER_SIZE_BOTTLE_MAP[size] || null;
  }

  var BOTTLE_PROMPT_SESSION_PREFIX = 're_bottle_prompt_shown_';

  // ---------- Free shipping bar: always-offer upsell pool ----------

  // Shown on the cart page underneath the shipping bar, in this order,
  // skipping anything already in the cart. Useful to any customer
  // regardless of which additive brand they use — unlike recommending a
  // different brand's holder, which only helps if they happen to use that
  // brand. priceCents must stay in sync with the live variant price.
  var ALWAYS_OFFER_POOL = [
    {
      variantId: 53094769361109,
      productHandle: 'magnetic-funnel-holder-capless-fuel-door-funnel-storage',
      title: 'Magnetic Funnel Holder',
      priceCents: 1499,
      price: '$14.99',
      imageUrl: 'https://cdn.shopify.com/s/files/1/0739/7680/7637/files/FunnelHolder_2.jpg?v=1770331292'
    },
    {
      variantId: FORD_PLATE_VARIANT_ID,
      productHandle: FORD_PLATE_HANDLE,
      title: FORD_PLATE_TITLE,
      priceCents: 2999,
      price: FORD_PLATE_PRICE,
      imageUrl: FORD_PLATE_IMAGE_URL
    },
    {
      variantId: 54624098910421, // Large — default quick-add size
      productHandle: 'revolution-engineering-t-shirt',
      title: 'Revolution Engineering T-Shirt (Large)',
      priceCents: 2299,
      price: '$22.99',
      imageUrl: 'https://cdn.shopify.com/s/files/1/0739/7680/7637/files/Shirt_Front.png?v=1787682998'
    }
  ];

  // Ensure the shared modal/shipbar styles exist immediately on every page
  // load, not only after a popup has fired (the Liquid-rendered cart-page
  // shipping bar needs these styles from first paint).
  ensureStyles();

  function getRoutes() {
    return (window.routes && window.routes.cart_add_url)
      ? window.routes
      : { cart_add_url: '/cart/add.js', cart_url: '/cart.js' };
  }

  // ---------- helpers ----------

  function isFordPlate(item) {
    if (!item) return false;
    return item.handle === FORD_PLATE_HANDLE ||
      Number(item.variant_id || item.id) === FORD_PLATE_VARIANT_ID;
  }

  function variantText(item) {
    var parts = [item.variant_title, item.title, item.product_title];
    if (Array.isArray(item.options_with_values)) {
      item.options_with_values.forEach(function (o) {
        parts.push(o.value);
      });
    }
    return parts.filter(Boolean).join(' / ').toLowerCase();
  }

  function isMagnetItem(item) {
    if (isFordPlate(item)) return false;
    return variantText(item).indexOf('magnet') !== -1;
  }

  function isScrewItem(item) {
    if (isFordPlate(item)) return false;
    return variantText(item).indexOf('screw') !== -1;
  }

  function getHolderBottleSize(item) {
    if (!item) return null;
    var vid = Number(item.variant_id || item.id);
    return HOLDER_VARIANT_SIZE_MAP[vid] || null;
  }

  function getCart() {
    return fetch(getRoutes().cart_url || '/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  // ---------- modal UI ----------

  function ensureStyles() {
    if (document.getElementById('re-cart-modal-styles')) return;
    var style = document.createElement('style');
    style.id = 're-cart-modal-styles';
    style.textContent =
      '.re-modal-overlay{position:fixed;inset:0;background:rgba(0,0,0,.55);' +
      'display:flex;align-items:center;justify-content:center;z-index:99999;padding:20px;}' +
      '.re-modal-box{background:#fff;max-width:420px;width:100%;border-radius:8px;' +
      'padding:24px;box-shadow:0 10px 40px rgba(0,0,0,.25);font-family:inherit;}' +
      '.re-modal-box h3{margin:0 0 12px;font-size:18px;line-height:1.3;}' +
      '.re-modal-box p{margin:0 0 18px;font-size:14px;line-height:1.5;color:#333;}' +
      '.re-modal-actions{display:flex;gap:10px;flex-wrap:wrap;justify-content:flex-end;}' +
      '.re-modal-btn{cursor:pointer;border-radius:4px;padding:10px 16px;font-size:14px;' +
      'font-weight:600;border:1px solid transparent;}' +
      '.re-modal-btn-primary{background:#1a1a1a;color:#fff;}' +
      '.re-modal-btn-secondary{background:#fff;color:#1a1a1a;border-color:#ccc;}' +
      '.re-modal-btn:hover{opacity:.85;}' +
      '@keyframes reSidePopIn{from{transform:translateX(24px);opacity:0;}to{transform:translateX(0);opacity:1;}}' +
      '.re-side-popup{position:fixed;bottom:20px;right:20px;z-index:99998;max-width:300px;' +
      'background:#fff;border-radius:10px;box-shadow:0 8px 30px rgba(0,0,0,.2);' +
      'font-family:inherit;animation:reSidePopIn .35s ease-out;transition:bottom .25s ease-out;}' +
      '.re-side-header{display:flex;align-items:flex-start;justify-content:space-between;' +
      'gap:8px;padding:14px 14px 0 14px;}' +
      '.re-side-header p{margin:0;font-size:14px;font-weight:700;line-height:1.3;color:#1a1a1a;}' +
      '.re-side-close{cursor:pointer;border:none;background:none;font-size:18px;line-height:1;' +
      'color:#888;padding:0 0 0 8px;}' +
      '.re-side-close:hover{color:#1a1a1a;}' +
      '.re-side-card{display:flex;gap:10px;align-items:center;padding:12px 14px 14px 14px;}' +
      '.re-side-img{width:60px;height:60px;object-fit:cover;border-radius:6px;flex-shrink:0;' +
      'border:1px solid #eee;}' +
      '.re-side-info{flex:1;min-width:0;}' +
      '.re-side-info .re-side-title{display:block;font-size:13px;font-weight:600;color:#1a1a1a;' +
      'line-height:1.3;margin-bottom:2px;text-decoration:none;}' +
      '.re-side-info .re-side-title:hover{text-decoration:underline;}' +
      '.re-side-info .re-side-price{display:block;font-size:12px;color:#666;margin-bottom:8px;}' +
      '.re-side-img-link{display:block;flex-shrink:0;}' +
      '.re-side-btn{cursor:pointer;border:1px solid #1a1a1a;background:#1a1a1a;color:#fff;' +
      'border-radius:4px;padding:6px 12px;font-size:12px;font-weight:600;width:100%;}' +
      '.re-side-btn:hover{opacity:.85;}' +
      '.re-side-btn[disabled]{opacity:.6;cursor:default;}' +
      '.re-shipbar-mount{display:block;}' +
      '.re-shipbar{font-family:inherit;margin:0 0 16px;}' +
      '.re-shipbar-text{margin:0 0 6px;font-size:13px;color:#1a1a1a;line-height:1.4;}' +
      '.re-shipbar-text strong{font-weight:700;}' +
      '.re-shipbar-track{width:100%;height:6px;background:#e5e5e5;border-radius:999px;overflow:hidden;}' +
      '.re-shipbar-fill{height:100%;background:#1a1a1a;border-radius:999px;transition:width .4s ease, background .3s ease;}' +
      '.re-upsell-list{margin:0 0 16px;display:flex;flex-direction:column;gap:8px;}' +
      '.re-upsell{display:flex;gap:14px;align-items:center;padding:12px;' +
      'border:1px solid #e5e5e5;border-radius:8px;background:#fafafa;}' +
      '.re-upsell-img{width:88px;height:88px;object-fit:cover;border-radius:6px;flex-shrink:0;' +
      'border:1px solid #eee;background:#fff;}' +
      '.re-upsell-info{flex:1;min-width:0;}' +
      '.re-upsell-reason{display:block;font-size:11.5px;font-weight:600;text-transform:uppercase;' +
      'letter-spacing:.03em;color:#16a34a;margin-bottom:2px;}' +
      '.re-upsell-title{display:block;font-size:13px;font-weight:600;color:#1a1a1a;' +
      'text-decoration:none;line-height:1.3;}' +
      '.re-upsell-title:hover{text-decoration:underline;}' +
      '.re-upsell-price{display:block;font-size:12px;color:#666;margin-top:2px;}' +
      '.re-upsell-btn{cursor:pointer;border:1px solid #1a1a1a;background:#1a1a1a;color:#fff;' +
      'border-radius:4px;padding:8px 14px;font-size:12px;font-weight:600;flex-shrink:0;white-space:nowrap;}' +
      '.re-upsell-btn:hover{opacity:.85;}' +
      '.re-upsell-btn[disabled]{opacity:.6;cursor:default;}';
    document.head.appendChild(style);
  }

  var SIDE_POPUP_BASE_BOTTOM = 20;
  var SIDE_POPUP_GAP = 12;

  // Stack multiple side popups (e.g. Ford plate + bottle upsell firing from
  // the same add-to-cart) by measuring each popup's *actual* rendered
  // height and stacking the next one above it with a fixed gap. A hardcoded
  // pixel offset doesn't work here because popup height varies with how
  // many lines the title/header text wraps to, which was causing the
  // bottle-upsell popup to overlap the Ford plate popup.
  function layoutSidePopups() {
    var popups = document.querySelectorAll('.re-side-popup');
    var offset = SIDE_POPUP_BASE_BOTTOM;
    popups.forEach(function (popup) {
      popup.style.bottom = offset + 'px';
      offset += popup.getBoundingClientRect().height + SIDE_POPUP_GAP;
    });
  }

  function openSidePopup(innerHtml) {
    ensureStyles();
    var box = document.createElement('div');
    box.className = 're-side-popup';
    box.setAttribute('role', 'status');
    box.innerHTML = innerHtml;

    function close() {
      if (box.parentNode) box.parentNode.removeChild(box);
      layoutSidePopups();
    }

    document.body.appendChild(box);
    layoutSidePopups();
    return { box: box, close: close };
  }

  function openModal(innerHtml, options) {
    ensureStyles();
    options = options || {};
    var overlay = document.createElement('div');
    overlay.className = 're-modal-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');

    var box = document.createElement('div');
    box.className = 're-modal-box';
    box.innerHTML = innerHtml;
    overlay.appendChild(box);

    function close() {
      if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
      document.removeEventListener('keydown', onKeydown);
    }

    function onKeydown(e) {
      if (e.key === 'Escape' && options.dismissible !== false) close();
    }

    if (options.dismissible !== false) {
      overlay.addEventListener('click', function (e) {
        if (e.target === overlay) close();
      });
      document.addEventListener('keydown', onKeydown);
    }

    document.body.appendChild(overlay);

    var firstBtn = box.querySelector('button');
    if (firstBtn) firstBtn.focus();

    return { overlay: overlay, box: box, close: close };
  }

  // ---------- Ford upsell flow ----------

  function showFordSidePopup() {
    var popup = openSidePopup(
      '<div class="re-side-header">' +
      '<p>2017+ Ford? You’ll need one of these too!</p>' +
      '<button type="button" class="re-side-close" aria-label="Dismiss" data-re="close">&times;</button>' +
      '</div>' +
      '<div class="re-side-card">' +
      '<a class="re-side-img-link" href="' + FORD_PLATE_URL + '">' +
      '<img class="re-side-img" src="' + FORD_PLATE_IMAGE_URL + '" alt="' + FORD_PLATE_TITLE + '">' +
      '</a>' +
      '<div class="re-side-info">' +
      '<a class="re-side-title" href="' + FORD_PLATE_URL + '">' + FORD_PLATE_TITLE + '</a>' +
      '<span class="re-side-price">' + FORD_PLATE_PRICE + ' &middot; aluminum body needs a steel surface for the magnet to grip</span>' +
      '<button type="button" class="re-side-btn" data-re="add">Add to Cart</button>' +
      '</div>' +
      '</div>'
    );

    popup.box.querySelector('[data-re="close"]').addEventListener('click', popup.close);

    popup.box.querySelector('[data-re="add"]').addEventListener('click', function (e) {
      addFordPlateToCart(popup, e.currentTarget);
    });
  }

  function addFordPlateToCart(popup, button) {
    if (button) {
      button.disabled = true;
      button.textContent = 'Adding…';
    }
    var routes = getRoutes();
    fetch(routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: FORD_PLATE_VARIANT_ID, quantity: 1, properties: { _source: 'ford-popup' } })
    })
      .then(function (r) { return r.json(); })
      .then(function () {
        refreshCartIconBubble();
        if (button) {
          button.textContent = 'Added ✓';
        }
        setTimeout(popup.close, 1600);
      })
      .catch(function () {
        if (button) {
          button.disabled = false;
          button.textContent = 'Add to Cart';
        }
      });
  }

  // ---------- Hot Shot's Secret bottle upsell flow ----------

  function showBottleSidePopup(bottle) {
    var popup = openSidePopup(
      '<div class="re-side-header">' +
      '<p>Don’t forget the additive!</p>' +
      '<button type="button" class="re-side-close" aria-label="Dismiss" data-re="close">&times;</button>' +
      '</div>' +
      '<div class="re-side-card">' +
      '<a class="re-side-img-link" href="/products/' + bottle.productHandle + '">' +
      '<img class="re-side-img" src="' + bottle.imageUrl + '" alt="' + bottle.title + '">' +
      '</a>' +
      '<div class="re-side-info">' +
      '<a class="re-side-title" href="/products/' + bottle.productHandle + '">' + bottle.title + '</a>' +
      '<span class="re-side-price">' + bottle.price + ' &middot; sized to fit the holder you just added</span>' +
      '<button type="button" class="re-side-btn" data-re="add">Add to Cart</button>' +
      '</div>' +
      '</div>'
    );

    popup.box.querySelector('[data-re="close"]').addEventListener('click', popup.close);

    popup.box.querySelector('[data-re="add"]').addEventListener('click', function (e) {
      addBottleToCart(bottle, popup, e.currentTarget);
    });
  }

  function addBottleToCart(bottle, popup, button) {
    if (button) {
      button.disabled = true;
      button.textContent = 'Adding…';
    }
    var routes = getRoutes();
    fetch(routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: bottle.variantId, quantity: 1, properties: { _source: 'bottle-popup' } })
    })
      .then(function (r) { return r.json(); })
      .then(function () {
        refreshCartIconBubble();
        if (button) {
          button.textContent = 'Added ✓';
        }
        setTimeout(popup.close, 1600);
      })
      .catch(function () {
        if (button) {
          button.disabled = false;
          button.textContent = 'Add to Cart';
        }
      });
  }

  function maybeShowBottlePrompt(addedItems, cart) {
    addedItems.forEach(function (item) {
      var size = getHolderBottleSize(item);
      if (!size) return;
      var bottle = getBottleForSize(size);
      if (!bottle) return;

      var sessionKey = BOTTLE_PROMPT_SESSION_PREFIX + size;
      if (sessionStorage.getItem(sessionKey) === '1') return;

      var alreadyHasBottle = ((cart && cart.items) || []).some(function (cartItem) {
        return cartItem.handle === bottle.productHandle;
      });
      if (alreadyHasBottle) return;

      sessionStorage.setItem(sessionKey, '1');
      showBottleSidePopup(bottle);
    });
  }

  function refreshCartIconBubble() {
    var routes = getRoutes();
    var url = (routes.cart_url || '/cart') + '?sections=cart-icon-bubble';
    fetch(url)
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var html = data && data['cart-icon-bubble'];
        if (!html) return;
        var temp = document.createElement('div');
        temp.innerHTML = html;
        var newEl = temp.querySelector('#cart-icon-bubble');
        var oldEl = document.querySelector('#cart-icon-bubble');
        if (newEl && oldEl) oldEl.replaceWith(newEl);
      })
      .catch(function () {});
  }

  // ---------- Free shipping progress bar ----------

  function formatMoneyCents(cents) {
    var dollars = (cents / 100).toFixed(2);
    return '$' + dollars;
  }

  function buildShippingBarHtml(cart) {
    var total = (cart && cart.total_price) || 0;
    var threshold = FREE_SHIPPING_THRESHOLD_CENTS;
    var remaining = threshold - total;
    var percent = (total / threshold) * 100;
    if (percent > 100) percent = 100;
    if (percent < 0) percent = 0;

    var unlocked = remaining <= 0;
    var fillColor = unlocked ? '#16a34a' : '#1a1a1a';

    var text = remaining > 0
      ? 'You’re <strong>' + formatMoneyCents(remaining) + '</strong> away from free shipping!'
      : '🎉 You’ve unlocked free shipping!';

    return (
      '<div class="re-shipbar">' +
      '<p class="re-shipbar-text">' + text + '</p>' +
      '<div class="re-shipbar-track" style="width:100%;height:6px;background:#e5e5e5;border-radius:999px;overflow:hidden;display:block;">' +
      '<div class="re-shipbar-fill" style="width:' + percent + '%;height:100%;background:' + fillColor + ';border-radius:999px;display:block;"></div>' +
      '</div>' +
      '</div>'
    );
  }

  // ---------- Free shipping upsell: pick + render + add ----------

  // Returns an array of picks — the Hot Shot's Secret pairing (if
  // applicable) followed by the always-offer pool, skipping anything
  // already in the cart. Empty array once free shipping is unlocked.
  function pickUpsellCandidates(cart) {
    var total = (cart && cart.total_price) || 0;
    var remaining = FREE_SHIPPING_THRESHOLD_CENTS - total;
    if (remaining <= 0) return [];

    var items = (cart && cart.items) || [];

    function alreadyInCart(variantId) {
      return items.some(function (cartItem) {
        return Number(cartItem.variant_id || cartItem.id) === variantId;
      });
    }

    var picks = [];

    // Priority: a Hot Shot's Secret holder in the cart without its
    // matching bottle. Relevant regardless of exact price fit.
    for (var i = 0; i < items.length; i++) {
      var size = getHolderBottleSize(items[i]);
      if (!size) continue;
      var bottle = getBottleForSize(size);
      if (!bottle) continue;
      if (alreadyInCart(bottle.variantId)) continue;
      picks.push({
        variantId: bottle.variantId,
        productHandle: bottle.productHandle,
        title: bottle.title,
        price: bottle.price,
        imageUrl: bottle.imageUrl,
        reason: 'Pairs with the holder in your cart'
      });
      break; // one pairing suggestion is enough
    }

    // Always offer these, skipping anything already in the cart.
    ALWAYS_OFFER_POOL.forEach(function (candidate) {
      if (alreadyInCart(candidate.variantId)) return;
      picks.push({
        variantId: candidate.variantId,
        productHandle: candidate.productHandle,
        title: candidate.title,
        price: candidate.price,
        imageUrl: candidate.imageUrl,
        reason: candidate.priceCents >= remaining ? 'Get free shipping' : 'Get closer to free shipping'
      });
    });

    return picks;
  }

  function buildUpsellItemHtml(pick) {
    return (
      '<div class="re-upsell">' +
      '<a href="/products/' + pick.productHandle + '">' +
      '<img class="re-upsell-img" src="' + pick.imageUrl + '" alt="' + pick.title + '">' +
      '</a>' +
      '<div class="re-upsell-info">' +
      '<span class="re-upsell-reason">' + pick.reason + '</span>' +
      '<a class="re-upsell-title" href="/products/' + pick.productHandle + '">' + pick.title + '</a>' +
      '<span class="re-upsell-price">' + pick.price + '</span>' +
      '</div>' +
      '<button type="button" class="re-upsell-btn" data-re-upsell-add data-variant-id="' + pick.variantId + '">Add</button>' +
      '</div>'
    );
  }

  function buildUpsellHtml(picks) {
    if (!picks || !picks.length) return '';
    return '<div class="re-upsell-list">' + picks.map(buildUpsellItemHtml).join('') + '</div>';
  }

  function addUpsellToCart(variantId, button) {
    if (button) {
      button.disabled = true;
      button.textContent = 'Adding…';
    }
    var routes = getRoutes();
    fetch(routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: Number(variantId), quantity: 1, properties: { _source: 'cart-upsell' } })
    })
      .then(function (r) {
        if (!r.ok) throw new Error('add failed');
        return r.json();
      })
      .then(function () {
        // Reload so the cart items table, totals, and shipping bar all
        // reflect the new line item — simpler and more reliable here than
        // patching the items table fragment in place.
        window.location.reload();
      })
      .catch(function () {
        if (button) {
          button.disabled = false;
          button.textContent = 'Add';
        }
      });
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest && event.target.closest('[data-re-upsell-add]');
    if (!button) return;
    event.preventDefault();
    addUpsellToCart(button.getAttribute('data-variant-id'), button);
  });

  function updateShippingBar(cart) {
    ensureStyles();
    var mounts = document.querySelectorAll('.re-shipbar-mount');
    if (!mounts.length) return;
    var hasItems = !!(cart && cart.item_count > 0);
    var html = '';
    if (hasItems) {
      html = buildShippingBarHtml(cart) + buildUpsellHtml(pickUpsellCandidates(cart));
    }
    mounts.forEach(function (mount) {
      mount.innerHTML = html;
    });
  }

  function onCartUpdated(cart) {
    checkIncompatibility(cart);
    updateShippingBar(cart);
  }

  // ---------- Incompatibility warning flow ----------

  function showIncompatibilityModal() {
    var modal = openModal(
      '<h3>Heads up: you don’t need both</h3>' +
      '<p>Your cart has both the Ford Magnetic Mount Plate and a screw-on additive holder. ' +
      'The mount plate exists to give magnetic holders a steel surface to grip on Ford’s aluminum truck ' +
      'body — magnets don’t stick to aluminum on their own. A screw-on holder mounts with its own ' +
      'hardware and doesn’t rely on a magnet, so it doesn’t need the plate. ' +
      'These two products are <strong>not compatible with each other</strong> and work ' +
      '<strong>independently</strong>. Both will still work fine in your cart, just not together.</p>' +
      '<div class="re-modal-actions">' +
      '<button type="button" class="re-modal-btn re-modal-btn-primary" data-re="ok">OK</button>' +
      '</div>',
      { dismissible: false }
    );
    modal.box.querySelector('[data-re="ok"]').addEventListener('click', modal.close);
  }

  function checkIncompatibility(cart) {
    var items = (cart && cart.items) || [];
    var hasFordPlate = items.some(isFordPlate);
    var hasScrew = items.some(isScrewItem);
    var comboPresent = hasFordPlate && hasScrew;

    if (comboPresent) {
      if (sessionStorage.getItem(INCOMPAT_SHOWN_SESSION_KEY) !== '1') {
        sessionStorage.setItem(INCOMPAT_SHOWN_SESSION_KEY, '1');
        showIncompatibilityModal();
      }
    } else {
      sessionStorage.removeItem(INCOMPAT_SHOWN_SESSION_KEY);
    }
  }

  function maybeShowFordPrompt(addedItems, cart) {
    if (sessionStorage.getItem(FORD_PROMPT_SESSION_KEY) === '1') return;
    var addedMagnet = addedItems.some(isMagnetItem);
    if (!addedMagnet) return;
    var alreadyHasPlate = ((cart && cart.items) || []).some(isFordPlate);
    if (alreadyHasPlate) return;
    sessionStorage.setItem(FORD_PROMPT_SESSION_KEY, '1');
    showFordSidePopup();
  }

  // ---------- cart mutation interception ----------

  function normalizeAddedItems(data) {
    if (!data) return [];
    if (Array.isArray(data.items)) return data.items;
    if (data.id || data.variant_id) return [data];
    return [];
  }

  function handleCartAddResponse(data) {
    var addedItems = normalizeAddedItems(data);
    getCart().then(function (cart) {
      if (addedItems.length) {
        maybeShowFordPrompt(addedItems, cart);
        maybeShowBottlePrompt(addedItems, cart);
      }
      onCartUpdated(cart);
    });
  }

  function handleGenericCartMutation() {
    getCart().then(onCartUpdated);
  }

  var CART_MUTATION_RE = /\/cart\/(add|change|update)(\.js)?(\?|$)/;
  var CART_ADD_RE = /\/cart\/add(\.js)?(\?|$)/;

  var originalFetch = window.fetch ? window.fetch.bind(window) : null;
  if (originalFetch) {
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var isAdd = CART_ADD_RE.test(url);
      var isMutation = CART_MUTATION_RE.test(url);
      var promise = originalFetch(input, init);

      if (isAdd || isMutation) {
        promise.then(function (response) {
          if (!response.ok) return;
          response.clone().json().then(function (data) {
            if (isAdd) {
              handleCartAddResponse(data);
            } else {
              handleGenericCartMutation();
            }
          }).catch(function () {});
        }).catch(function () {});
      }

      return promise;
    };
  }

  // Also catch classic form submissions (non-fetch) to /cart/add by checking
  // on page load — covers cart page reloads and no-JS-form fallbacks.
  document.addEventListener('DOMContentLoaded', function () {
    getCart().then(function (cart) {
      onCartUpdated(cart);
    }).catch(function () {});
  });
})();
